import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import ProfilePhoto from './ProfilePhoto';
import MenuButton from './MenuButton';
import Skeleton from './Skeleton';
import { useNavigate, Link } from 'react-router-dom';
import { Settings, LogOut } from 'lucide-react';

interface TopNavbarProps {
  isMenuOpen: boolean;
  onMenuToggle: () => void;
}

export default function TopNavbar({ isMenuOpen, onMenuToggle }: TopNavbarProps) {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: settings } = await supabase
        .from('settings')
        .select('company_name, profile_photo_url')
        .eq('user_id', user.id)
        .maybeSingle();

      if (settings) {
        setCompanyName(settings.company_name);
        setProfilePhotoUrl(settings.profile_photo_url);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePhotoUpdate = (url: string) => {
    setProfilePhotoUrl(url);
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="sticky top-0 z-40 bg-gray-900 border-b border-gray-800 h-14">
      <div className="px-4 h-full flex items-center justify-between">
        {/* Left side - Profile photo and company name */}
        <div className="flex items-center space-x-3">
          <div className="lg:hidden">
            {loading ? (
              <Skeleton className="h-7 w-7 rounded-full" />
            ) : (
              <ProfilePhoto
                url={profilePhotoUrl}
                size="sm"
                onUpdate={handleProfilePhotoUpdate}
                editable={false}
              />
            )}
          </div>
          {loading ? (
            <Skeleton className="h-5 w-28" />
          ) : (
            <h1 className="text-base font-semibold text-white truncate max-w-[180px] lg:max-w-none">
              {companyName || 'Administratie'}
            </h1>
          )}
        </div>

        {/* Right side - Menu button on mobile, profile photo on desktop */}
        <div className="flex items-center">
          {/* Menu button on mobile */}
          <div className="lg:hidden">
            <MenuButton
              isOpen={isMenuOpen}
              onClick={onMenuToggle}
              className="w-7 h-7"
            />
          </div>

          {/* Profile photo on desktop */}
          <div className="hidden lg:block relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center focus:outline-none"
              aria-label="Open user menu"
            >
              <ProfilePhoto
                url={profilePhotoUrl}
                size="sm"
                onUpdate={handleProfilePhotoUpdate}
                editable={false}
              />
            </button>

            {/* Dropdown menu */}
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-44 bg-gray-800 rounded-lg border border-gray-700 z-50 py-1 shadow-lg animate-scale-in">
                <button
                  onClick={() => {
                    navigate('/settings');
                    setShowDropdown(false);
                  }}
                  className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded mx-1 transition-all duration-200"
                >
                  <Settings className="h-3.5 w-3.5 mr-2" />
                  Instellingen
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded mx-1 transition-all duration-200"
                >
                  <LogOut className="h-3.5 w-3.5 mr-2" />
                  Uitloggen
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}